import { useEffect, useId, useMemo, useRef, useState } from 'react'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { initialsOf } from '../lib/initials'
import { messageOf } from '../lib/errors'
import { useDismiss } from '../lib/useDismiss'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { Dialog } from '../components/Dialog'
import { FilterChips, type FilterChip } from '../components/FilterChips'
import { Picker } from '../components/Picker'
import { SearchIcon } from '../components/icons'
import {
  AREA_LABEL,
  DEFAULT_FILTERS,
  EMPTY_FILTERS,
  KIND_HINT,
  KIND_LABEL,
  PAGE_SIZE,
  actorText,
  areaLabel,
  changeKeys,
  describeChanges,
  fullWhen,
  humanKey,
  loadAuditActions,
  loadAuditPage,
  searchAuditPeople,
  shortWhen,
  showValue,
  subjectText,
  type AuditAction,
  type AuditFilters,
  type AuditKind,
  type AuditPerson,
  type AuditPersonAs,
  type AuditRow,
} from '../lib/audit'

/**
 * The audit log — #/admin/audit (196, ACC-8). `audit.read`, tenant_admin only.
 *
 * David, 13 Oct: "an admin audit page, filters for person, activity or action,
 * edits, writes". Every row is a write the platform recorded — the log holds
 * no reads (rule 8) — so "writes" is the whole page, and "edits" is the Admin
 * action kind plus each row's Changes, where the writer kept both sides.
 *
 * ── PEOPLE AND ROLES' TREATMENT ─────────────────────────────────────────────
 *
 * The same shape as routes/Admin.tsx and AdminStars.tsx: a thin count row,
 * then search and pill filters with their choices as ×-able chips, one table,
 * and one row at a time in the right-hand tray. Unlike those two, FILTERING IS
 * SERVER-SIDE: the log is thousands of rows (1,679 invitations on 13 Oct
 * alone) and grows, so the page holds one page of 100 and asks for the next.
 *
 * ── IT OPENS ON ADMIN ACTIONS ───────────────────────────────────────────────
 *
 * David chose "everything, filterable" with admin actions first: what an
 * administrator did is what an audit is usually asked about, and without the
 * default the first screen is the HR sync's invitations. The Kind chip says
 * the list is filtered, and × on it shows everything.
 *
 * ── PERSON IS A SEARCH, NOT A LIST ──────────────────────────────────────────
 *
 * ~1,000 people have held a membership. The person filter asks the server
 * (audit_log_people) as you type, offers suspended people and the BambooHR
 * Sync principal too — their acts are exactly what an audit is asked about —
 * and asks which way round: did it, was affected, or either.
 */

const KINDS: AuditKind[] = ['admin', 'staff', 'system']
const PERSON_AS_LABEL: Record<AuditPersonAs, string> = {
  either: 'Did it or was affected',
  actor: 'Did it',
  subject: 'Was affected',
}
const KIND_TONE: Record<AuditKind, string> = { admin: 'ok', staff: 'pending', system: 'muted' }

type Group = 'Kind' | 'Area' | 'Action' | 'Person' | 'From' | 'To'

export function AdminAudit({ me }: { me: Me }) {
  const canAudit = can(me, 'audit.read')

  const [filters, setFilters] = useState<AuditFilters>(DEFAULT_FILTERS)
  const [personName, setPersonName] = useState<string | null>(null)
  const [searchDraft, setSearchDraft] = useState('')
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [actions, setActions] = useState<AuditAction[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<number | null>(null)


  // The search box waits for a pause: every keystroke would be a query over
  // the whole log.
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.search === searchDraft ? f : { ...f, search: searchDraft })), 350)
    return () => clearTimeout(t)
  }, [searchDraft])

  // The action list, once: its counts are for the whole log, not the filter.
  useEffect(() => {
    if (!canAudit) return
    let live = true
    loadAuditActions()
      .then((a) => live && setActions(a))
      .catch((e) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [canAudit])

  // The first page, on every filter change. An answer to an older question
  // that lands late is dropped, so the table always matches the chips.
  const ask = useRef(0)
  useEffect(() => {
    if (!canAudit) return
    const mine = ++ask.current
    setRows(null)
    setError(null)
    loadAuditPage(filters)
      .then((r) => {
        if (ask.current !== mine) return
        setRows(r)
        setHasMore(r.length === PAGE_SIZE)
      })
      .catch((e) => ask.current === mine && setError(messageOf(e)))
  }, [canAudit, filters])

  async function loadOlder() {
    if (!rows || rows.length === 0) return
    const mine = ask.current
    setLoadingMore(true)
    try {
      const more = await loadAuditPage(filters, rows[rows.length - 1].id)
      if (ask.current !== mine) return
      setRows([...rows, ...more])
      setHasMore(more.length === PAGE_SIZE)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setLoadingMore(false)
    }
  }

  const set = (patch: Partial<AuditFilters>) => setFilters((f) => ({ ...f, ...patch }))
  function choosePerson(id: string | null, name: string | null, as?: AuditPersonAs) {
    setPersonName(name)
    set({ personId: id, ...(as ? { personAs: as } : {}) })
  }

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Audit log"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={canAudit}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  const actionLabel = useMemo(() => new Map(actions.map((a) => [a.action, a.label])), [actions])
  const actionOptions = useMemo(
    () =>
      actions
        .filter((a) => filters.areas.length === 0 || filters.areas.includes(a.area))
        .map((a) => ({ value: a.action, label: a.label, hint: `${areaLabel(a.area)} · ${a.action}`, count: a.events })),
    [actions, filters.areas],
  )

  if (!canAudit) {
    return shell(
      <Card title="Audit log">
        <p className="empty">The audit log needs the Tenant Administrator role.</p>
      </Card>,
    )
  }

  const chips: FilterChip[] = [
    ...filters.kinds.map((v) => ({ group: 'Kind', value: v, label: KIND_LABEL[v as AuditKind] ?? v })),
    ...filters.areas.map((v) => ({ group: 'Area', value: v, label: areaLabel(v) })),
    ...filters.actions.map((v) => ({ group: 'Action', value: v, label: actionLabel.get(v) ?? v })),
    ...(filters.personId
      ? [{ group: 'Person', value: filters.personId, label: `${personName ?? 'Someone'} · ${PERSON_AS_LABEL[filters.personAs]}` }]
      : []),
    ...(filters.fromDay ? [{ group: 'From', value: filters.fromDay, label: filters.fromDay }] : []),
    ...(filters.toDay ? [{ group: 'To', value: filters.toDay, label: filters.toDay }] : []),
  ]

  function removeChip(c: FilterChip) {
    const g = c.group as Group
    if (g === 'Kind') set({ kinds: filters.kinds.filter((x) => x !== c.value) })
    if (g === 'Area') set({ areas: filters.areas.filter((x) => x !== c.value) })
    if (g === 'Action') set({ actions: filters.actions.filter((x) => x !== c.value) })
    if (g === 'Person') choosePerson(null, null)
    if (g === 'From') set({ fromDay: '' })
    if (g === 'To') set({ toDay: '' })
  }

  function clearFilters() {
    setSearchDraft('')
    setPersonName(null)
    setFilters(EMPTY_FILTERS)
  }

  const filtered = chips.length > 0 || filters.search.trim() !== ''
  const open = rows?.find((r) => r.id === selected) ?? null

  return shell(
    <div className="people audit">
      {/* ── ROW 1: the count ─────────────────────────────────────────────── */}
      <div className="people__bar">
        <p className="audit__lede">Every act the platform records, newest first. Nothing here can be changed.</p>
        <div className="people__actions">
          <p className="people__count" aria-live="polite">
            {rows === null ? (
              'Loading…'
            ) : (
              <>
                <strong>{rows.length.toLocaleString()}</strong>
                {hasMore ? '+' : ''} {rows.length === 1 ? 'entry' : 'entries'}
                {filtered && <span className="people__countnote"> · filtered</span>}
              </>
            )}
          </p>
        </div>
      </div>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {/* ── ROW 2: search and the filters ────────────────────────────────── */}
      <div className="people__filters" role="search" aria-label="Filter the audit log">
        <label className="people__search">
          <span className="searchwrap__icon" aria-hidden="true">
            <SearchIcon size={15} />
          </span>
          <span className="visually-hidden">Search the audit log</span>
          <input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search actions, names, details"
            autoComplete="off"
          />
        </label>

        <PersonFilter
          personId={filters.personId}
          personName={personName}
          personAs={filters.personAs}
          onChoose={choosePerson}
          onAs={(as) => set({ personAs: as })}
        />
        <Picker
          variant="pill"
          label="Kind"
          searchPlaceholder="Admin, staff or automatic"
          options={KINDS.map((k) => ({ value: k, label: KIND_LABEL[k], hint: KIND_HINT[k] }))}
          selected={filters.kinds}
          onChange={(v) => set({ kinds: v })}
        />
        <Picker
          variant="pill"
          label="Area"
          searchPlaceholder="Area"
          options={Object.keys(AREA_LABEL).map((a) => ({ value: a, label: AREA_LABEL[a] }))}
          selected={filters.areas}
          onChange={(v) => set({ areas: v })}
        />
        <Picker
          variant="pill"
          label="Action"
          className="audit__actionpicker"
          searchPlaceholder="Find an action"
          options={actionOptions}
          selected={filters.actions}
          onChange={(v) => set({ actions: v })}
        />
        <label className="audit__day">
          <span>From</span>
          <input type="date" value={filters.fromDay} max={filters.toDay || undefined} onChange={(e) => set({ fromDay: e.target.value })} />
        </label>
        <label className="audit__day">
          <span>To</span>
          <input type="date" value={filters.toDay} min={filters.fromDay || undefined} onChange={(e) => set({ toDay: e.target.value })} />
        </label>
      </div>

      <FilterChips chips={chips} onRemove={removeChip} onClear={clearFilters} />

      <div className="people__tablecard">
        {rows === null && !error && (
          <p className="empty people__empty" aria-busy="true">
            Reading the log…
          </p>
        )}

        {rows !== null && rows.length === 0 && (
          <div className="people__empty">
            <p className="empty">{filtered ? 'Nothing in the log matches those filters.' : 'The log is empty.'}</p>
            {filtered && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="roles__tablewrap">
            <table className="admin__table people__table audit__table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Who</th>
                  <th scope="col">What</th>
                  <th scope="col">To what or whom</th>
                  <th scope="col">Area</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = selected === r.id
                  return (
                    <tr
                      key={r.id}
                      className={'people__row' + (isOpen ? ' is-selected' : '')}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, a, input, label')) return
                        setSelected(r.id)
                      }}
                    >
                      <td className="audit__when">
                        <time dateTime={r.occurredAt}>{shortWhen(r.occurredAt)}</time>
                      </td>
                      <td className={r.actorId ? undefined : 'audit__system'}>{actorText(r)}</td>
                      <th scope="row" className="roles__person">
                        <span className="roles__who">
                          <button
                            type="button"
                            className="roles__open"
                            aria-haspopup="dialog"
                            aria-expanded={isOpen}
                            onClick={() => setSelected(r.id)}
                          >
                            <span className="roles__name">{r.label}</span>
                          </button>
                          <span className="roles__meta">
                            <span className={`statuspill statuspill--${KIND_TONE[r.kind] ?? 'muted'}`}>{KIND_LABEL[r.kind] ?? r.kind}</span>
                          </span>
                        </span>
                      </th>
                      <td className="audit__subject">{subjectText(r)}</td>
                      <td className="audit__area">{areaLabel(r.area)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows !== null && hasMore && (
        <div className="audit__more">
          <button type="button" className="btn btn--quiet btn--sm" onClick={loadOlder} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Show ${PAGE_SIZE} older`}
          </button>
        </div>
      )}

      {open && (
        <Dialog open title={open.label} variant="tray" className="people__tray" onClose={() => setSelected(null)}>
          <AuditDetail
            row={open}
            onPerson={(id, name, as) => {
              setSelected(null)
              choosePerson(id, name, as)
            }}
            onAction={(action) => {
              setSelected(null)
              set({ actions: [action] })
            }}
          />
        </Dialog>
      )}
    </div>,
  )
}

/** One row, in full: who, what, to whom, when, and everything the writer kept. */
function AuditDetail({
  row,
  onPerson,
  onAction,
}: {
  row: AuditRow
  onPerson: (id: string, name: string, as: AuditPersonAs) => void
  onAction: (action: string) => void
}) {
  const changes = describeChanges(row.payload)
  const shown = changeKeys(row.payload)
  const facts = Object.entries(row.payload).filter(([k]) => !shown.has(k))
  const subjectPerson = row.subjectType === 'person' && row.subjectId ? row.subjectId : null

  return (
    <div className="audit__detail">
      <section className="panel__section">
        <dl className="noticetray__facts">
          <dt>When</dt>
          <dd>
            <time dateTime={row.occurredAt}>{fullWhen(row.occurredAt)}</time>
          </dd>

          <dt>Who</dt>
          <dd>{actorText(row)}</dd>

          <dt>What</dt>
          <dd>
            {row.label} <span className={`statuspill statuspill--${KIND_TONE[row.kind] ?? 'muted'}`}>{KIND_LABEL[row.kind] ?? row.kind}</span>
            <code className="audit__code">{row.action}</code>
          </dd>

          <dt>To what or whom</dt>
          <dd>
            {subjectText(row)}
            <span className="audit__hint">{humanKey(row.subjectType)}</span>
          </dd>

          <dt>Area</dt>
          <dd>{areaLabel(row.area)}</dd>

          <dt>Entry</dt>
          <dd>
            <code className="audit__code">#{row.id}</code>
          </dd>
        </dl>
      </section>

      {changes.length > 0 && (
        <section className="panel__section">
          <h3 className="panel__h3">Changes</h3>
          <table className="audit__changes">
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Was</th>
                <th scope="col">Now</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => (
                <tr key={`${c.field}-${i}`}>
                  <th scope="row">{c.field}</th>
                  <td>{showValue(c.was, row.people)}</td>
                  <td>{showValue(c.now, row.people)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {facts.length > 0 && (
        <section className="panel__section">
          <h3 className="panel__h3">Details</h3>
          <dl className="noticetray__facts">
            {facts.map(([k, v]) => (
              <Fact key={k} label={humanKey(k)} value={showValue(v, row.people)} />
            ))}
          </dl>
        </section>
      )}

      {facts.length === 0 && changes.length === 0 && (
        <p className="audit__hint">The writer recorded no details beyond who, what and when.</p>
      )}

      <section className="panel__section">
        <h3 className="panel__h3">Look further</h3>
        <div className="audit__links">
          {row.actorId && row.actorName && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => onPerson(row.actorId!, row.actorName!, 'actor')}>
              Everything {row.actorName} did
            </button>
          )}
          {subjectPerson && row.subjectLabel && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => onPerson(subjectPerson, row.subjectLabel!, 'subject')}>
              Everything done to {row.subjectLabel}
            </button>
          )}
          <button type="button" className="btn btn--quiet btn--sm" onClick={() => onAction(row.action)}>
            Every “{row.label}”
          </button>
        </div>
      </section>

      <details className="audit__raw">
        <summary>The entry as recorded</summary>
        <pre>{JSON.stringify({ action: row.action, subject_type: row.subjectType, subject_id: row.subjectId, actor_person_id: row.actorId, payload: row.payload }, null, 2)}</pre>
      </details>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  )
}

/**
 * The person filter: a pill like the Pickers beside it, whose panel searches
 * the server instead of a list it was handed. Single choice — "everything
 * about Ana" is one person — with the which-way-round question under it.
 */
function PersonFilter({
  personId,
  personName,
  personAs,
  onChoose,
  onAs,
}: {
  personId: string | null
  personName: string | null
  personAs: AuditPersonAs
  onChoose: (id: string | null, name: string | null) => void
  onAs: (as: AuditPersonAs) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<AuditPerson[] | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const baseId = useId()

  useDismiss(wrapRef, open, (viaKeyboard) => {
    setOpen(false)
    if (viaKeyboard) triggerRef.current?.focus()
  })

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    let live = true
    const t = setTimeout(() => {
      searchAuditPeople(query)
        .then((p) => {
          if (!live) return
          setFound(p)
          setFailed(null)
        })
        .catch((e) => live && setFailed(messageOf(e)))
    }, 250)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [open, query])

  return (
    <div className="picker picker--pill audit__person" ref={wrapRef}>
      <span className="visually-hidden" id={`${baseId}-label`}>
        Person
      </span>
      <button
        type="button"
        ref={triggerRef}
        className={`picker__trigger${personId ? ' picker__trigger--set' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby={`${baseId}-label ${baseId}-summary`}
        onClick={() => setOpen((o) => !o)}
      >
        <span id={`${baseId}-summary`} className="picker__summary">
          <span aria-hidden="true">Person</span>
          {personId && <span className="picker__pillvalue">{personName ?? 'chosen'}</span>}
        </span>
        <span className="picker__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div className="picker__panel" role="dialog" aria-label="Choose a person">
          <fieldset className="audit__as">
            <legend className="visually-hidden">Which way round</legend>
            {(['either', 'actor', 'subject'] as AuditPersonAs[]).map((as) => (
              <label key={as} className={`chip${personAs === as ? ' chip--on' : ''}`}>
                <input
                  type="radio"
                  className="visually-hidden"
                  name={`${baseId}-as`}
                  checked={personAs === as}
                  onChange={() => onAs(as)}
                />
                {PERSON_AS_LABEL[as]}
              </label>
            ))}
          </fieldset>
          <div className="picker__search">
            <input
              ref={inputRef}
              type="text"
              aria-label="Search people"
              placeholder="Type a name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          <ul className="picker__list">
            {failed && <li className="picker__none">{failed}</li>}
            {!failed && found === null && <li className="picker__none">Searching…</li>}
            {!failed && found?.length === 0 && <li className="picker__none">Nobody matches “{query}”.</li>}
            {!failed &&
              found?.map((p) => (
                <li key={p.personId}>
                  <button
                    type="button"
                    className={`picker__opt audit__personopt${p.personId === personId ? ' picker__opt--on' : ''}`}
                    onClick={() => {
                      onChoose(p.personId, p.displayName)
                      setOpen(false)
                      setQuery('')
                    }}
                  >
                    <span className="picker__tick" aria-hidden="true">
                      {p.personId === personId ? '✓' : ''}
                    </span>
                    <span className="picker__optlabel">
                      {p.displayName}
                      {(p.isService || !p.isActive) && (
                        <span className="picker__hint">{p.isService ? 'Service account' : 'No longer active'}</span>
                      )}
                    </span>
                    <span className="picker__count">{p.acts.toLocaleString()}</span>
                  </button>
                </li>
              ))}
          </ul>
          {personId && (
            <div className="audit__personclear">
              <button
                type="button"
                className="btn btn--quiet btn--xs"
                onClick={() => {
                  onChoose(null, null)
                  setOpen(false)
                }}
              >
                Anyone
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
